<template>
    <form @submit.prevent="handleSubmit" class="form-container advance-search-form" data-cy="advance-search-container">
        <div class="form-section">
            <div class="fields pt-4">
                <div class="flex flex-wrap w-full">
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-cover-id-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.cover_id') }}</ui-label>
                        <ui-input v-model="search_form.cover_id" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-cover-type-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.cover_type') }}</ui-label>
                        <ui-select v-model="search_form.cover_type" :options="cover_types" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-file-source-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.file_source') }}</ui-label>
                        <ui-input v-model="search_form.file_source" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-registration-no-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.reg_no') }}</ui-label>
                        <ui-input v-model="search_form.reg_no" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-chassis-no-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.chassis_no') }}</ui-label>
                        <ui-input v-model="search_form.chassis_no" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-engine-no-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.engine_no') }}</ui-label>
                        <ui-input v-model="search_form.engine_no" />
                    </div>
                    <div
                        v-if="section_type == 'site_admin'"
                        class="input-container mb-2 md:w-1/3 pr-2"
                        data-cy="covers-search-association-field"
                    >
                        <ui-label>{{ $t('covers.search_form.input_fields.association') }}</ui-label>
                        <ui-select
                            :searchable="true"
                            v-model="search_form.association_id"
                            :allowClear="true"
                            :options="association_list"
                        >
                        </ui-select>
                    </div>
                    <div
                        v-if="section_type == 'site_admin'"
                        class="input-container mb-2 md:w-1/3 pr-2"
                        data-cy="covers-search-insurer-field"
                    >
                        <ui-label>{{ $t('covers.search_form.input_fields.insurer') }}</ui-label>
                        <ui-select
                            v-if="section_type == 'site_admin'"
                            :searchable="true"
                            v-model="search_form.insurer_id"
                            :options="insurer_list"
                            :allowClear="true"
                        >
                        </ui-select>
                    </div>
                    <div
                        v-if="section_type == 'site_admin'"
                        class="input-container mb-2 md:w-1/3 pr-2"
                        data-cy="covers-search-intermediary-field"
                    >
                        <ui-label>{{ $t('covers.search_form.input_fields.intermediary') }}</ui-label>
                        <ui-select
                            :searchable="true"
                            v-model="search_form.intermediary_id"
                            :options="intermediary_list"
                            :allowClear="true"
                        >
                        </ui-select>
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-insurer-policy-number-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.insurer_policy_number') }}</ui-label>
                        <ui-input v-model="search_form.insurer_policy_number" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-expiry-date-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.cover_expiry_date') }}</ui-label>
                        <ui-date-range-picker-double v-model="search_form.cover_expiry_date" :allowClear="false" />
                    </div>
                    <div class="input-container mb-2 md:w-1/3 pr-2" data-cy="covers-search-imported-date-field">
                        <ui-label>{{ $t('covers.search_form.input_fields.imported_date') }}</ui-label>
                        <ui-date-range-picker-double v-model="search_form.imported_date" :allowClear="false" />
                    </div>
                </div>
            </div>
        </div>
        <div class="search-buttons items-end pt-4 self-end">
            <div class="flex w-full justify-between">
                <div>
                    <a-button data-cy="button-cancel" @click="handleClear" class="mx-2" :disabled="processing">
                        {{ $t('covers.search_form.buttons.clear') }}
                    </a-button>
                    <ui-submit-button class="mx-2" data-cy="button-find" :disabled="processing">
                        <span v-if="!processing">{{ $t('covers.search_form.buttons.find') }}</span>
                        <span v-else>{{ $t('covers.search_form.buttons.finding') }}</span>
                    </ui-submit-button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';

export default {
    components: {
        UiInput,
        UiLabel,
        UiSelect,
        UiSubmitButton,
        UiDateRangePickerDouble,
    },
    data() {
        return {
            association_list: [],
            insurer_list: [],
            intermediary_list: [],
            cover_types: [
                {
                    text: 'Certificate',
                    value: 'certificate',
                },
                {
                    text: 'Covernote',
                    value: 'covernote',
                },
            ],
        };
    },
    props: {
        processing: {
            type: Boolean,
            default: false,
        },
        search_form: {
            type: Object,
            default: {},
        },
        section_type: {
            type: String,
            default: '',
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        handleClear() {
            this.$emit('clear');
        },
        handleSubmit() {
            this.$emit('formSubmit');
        },
        fetch() {
            if (this.section_type == 'site_admin') {
                this.is_ready = false;
                let urls = [
                    this.$http.get('/api/admin/list-associations'),
                    this.$http.get('/api/admin/list-companies-insurers'),
                    this.$http.get('/api/admin/list-companies-intermediaries'),
                ];

                this.$http.all(urls).then(
                    this.$http.spread((association_list, insurer_list, intermediary_list) => {
                        this.association_list = [];
                        this.insurer_list = [];
                        this.intermediary_list = [];

                        association_list.data.data.forEach((record) => {
                            this.association_list.push({
                                text: record.name,
                                value: record.id,
                            });
                        });

                        insurer_list.data.data.forEach((record) => {
                            this.insurer_list.push({
                                text: record.company_name,
                                value: record.company_id,
                            });
                        });

                        intermediary_list.data.data.forEach((record) => {
                            this.intermediary_list.push({
                                text: record.company_name,
                                value: record.company_id,
                            });
                        });
                    }),
                );
            }
        },
    },
};
</script>
